// Pure phone-display formatting, extracted byte-for-byte from the App.tsx
// helpers `formatPhoneDisplay` / `formatCallTimer`. App.tsx imports these;
// format.test.ts embeds the legacy inline implementations verbatim and pins
// equality over a corpus, so any "improvement" here that changes output will
// fail the oracle. Six lazy phone chunks under src/views/phone/ still carry
// sanctioned local duplicates of formatPhoneDisplay — adopt this module there
// on their next internal change (docs/PHONE_ARCHITECTURE.md).

/**
 * Format raw digits as (123) 456-7890, handling a leading 1 as the US
 * country code (rendered as a "+1 " prefix). Non-digits are stripped first,
 * so already-formatted or punctuated input is safe to re-feed.
 *
 * Why the area-code paren stays UNCLOSED below 4 digits: the dialer
 * re-formats on every keystroke, so if the paren auto-closed at 3 digits,
 * backspacing the ")" would re-trigger the format and immediately re-add
 * it — backspace would get stuck at "(954)". Do not "fix" the unbalanced
 * paren; it is load-bearing for the keypad editing experience.
 */
export function formatPhoneDisplay(raw: string): string {
  let d = raw.replace(/[^\d]/g, '')
  if (d.length === 0) return ''
  // Leading 1 = US country code
  let prefix = ''
  if (d.startsWith('1') && d.length > 1) {
    prefix = '+1 '
    d = d.slice(1)
  } else if (d === '1') {
    return '+1'
  }
  // Only close the area-code paren once there's a 4th digit — otherwise
  // backspace gets stuck at "(954)" because deleting the ")" re-triggers
  // the format and immediately re-adds it.
  if (d.length <= 3) return prefix + `(${d}`
  if (d.length <= 6) return prefix + `(${d.slice(0, 3)}) ${d.slice(3)}`
  return prefix + `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`
}

/**
 * Format an elapsed-seconds call timer as m:ss ("0:07", "1:01"). Minutes are
 * intentionally NOT rolled into hours — a two-hour call reads "122:05",
 * matching the iOS call-timer look the emulated phone copies.
 */
export function formatCallTimer(secs: number): string {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * iPhone-style "who is this number?" label for an AMBIGUOUS phone match. One
 * phone number can belong to several CRM contacts, so we present it AS
 * ambiguous instead of silently picking one person:
 *
 *   0 matches  → ''                     (caller falls back to the bare number)
 *   1 match    → "Adrian"               (the contact name)
 *   2 matches  → "Adrian or Barney"     (both names joined by " or ")
 *   3+ matches → "Adrian or 3 others"   (first name + count of the rest)
 *
 * The "N others" count is (matches with a name) − 1, pluralized correctly
 * ("or 1 other" vs "or 2 others"). The contacts/lookup endpoint caps its
 * matches array, so the ceiling is the server cap minus one — acceptable; a
 * very-shared number reads e.g. "Adrian or 4 others".
 *
 * Shared by BOTH the keypad live-lookup (src/views/phone/Keypad.tsx) and the
 * outbound/active-call peer name (src/App.tsx) so the rule is identical.
 * Nameless matches are ignored — we can only show contacts that have a name.
 */
export function ambiguousContactLabel(
  matches: ReadonlyArray<{ name?: string | null } | null | undefined> | null | undefined,
): string {
  const names = (matches || [])
    .map((m) => (m?.name || '').trim())
    .filter((n) => n.length > 0)
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} or ${names[1]}`
  const firstName = names[0].split(/\s+/)[0]
  const others = names.length - 1
  return `${firstName} or ${others} ${others === 1 ? 'other' : 'others'}`
}
